/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// MUI Components
import { SelectChangeEvent } from '@mui/material';

// Libraries
import React, { useCallback, useEffect, useState } from 'react';

// Types
import {
  PaginatedResponse,
  Registration,
  RegistrationResponse,
} from '../types/RegistrationTable.type';

// Local Components
import { apiDelete, apiGet, apiPost } from '../utils/apiClient';
import {
  ServiceType,
  Response,
  Subtype,
  Subsubtype,
} from '../types/ServiceAccordion.type';
import { notifyAPIClient } from '../utils/toast';
import { SuccessResponse } from '../types/CourseStageAccordion.type';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

export const useRegistrationController = () => {
  // States
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType>({
    id: '-1',
    name: 'All',
    subtypes: [],
  });
  const [subTypeFilter, setSubTypeFilter] = useState<Subtype>({
    id: '-1',
    name: 'All',
    subsubtypes: [],
  });
  const [subSubTypeFilter, setSubSubTypeFilter] = useState<Subsubtype>({
    id: '-1',
    name: 'All',
    serviceFor: 'CHILD',
    price: 0,
    duration: 0,
  });
  const [serviceOptions, setServiceOptions] = useState<ServiceType[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // 'all' | 'anonymous' | 'non_anonymous'
  const [anonymousFilter, setAnonymousFilter] = useState<string>('all');
  // 'all' | 'CASH' | 'BANK_TRANSFER' | 'DEPOSIT'
  const [paymentMethodFilter, setPaymentMethodFilter] =
    useState<string>('all');
  // 'all' | 'invoiced' | 'not_invoiced'
  const [invoicedFilter, setInvoicedFilter] = useState<string>('all');
  const [createInvoiceLoading, setCreateInvoiceLoading] = useState(false);
  // const [orderBy, setOrderBy] = useState<keyof User>("id");
  // const [order, setOrder] = useState<Order>("asc");

  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);
  const [openRegistrationInfoDialog, setOpenRegistrationInfoDialog] =
    useState(false);

  const [openDeleteRegistrationDialog, setOpenDeleteRegistrationDialog] =
    useState(false);

  // Multiple registrations select
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRegistrations, setSelectedRegistrations] = useState<number[]>(
    [],
  );

  // Last sync time for registrations
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Data-loading state for the main list fetch
  const [loading, setLoading] = useState(true);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Download registrations list
  const [filterMonth, setFilterMonth] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleSelectRegistration = (reg: Registration) => {
    setSelectedRegistrations((prev) => {
      if (prev.includes(reg.id)) {
        const updated = prev.filter(
          (registrationId) => registrationId !== reg.id,
        );

        if (updated.length === 0) setSelectedUserId(null);
        return updated;
      } else {
        return [...prev, reg.id];
      }
    });
  };

  // const handleSelectAllRegistrations = (selectAll: boolean) => {
  //   if (selectAll) {
  //     setSelectedRegistrations(registrations.map((reg) => reg.id));
  //   } else {
  //     setSelectedRegistrations([]);
  //   }
  // };

  // Networking
  const fetchRegistrations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const monthParam = filterMonth ? `&month=${filterMonth}` : '';
      const yearParam = filterYear ? `&year=${filterYear}` : '';
      const res = await apiGet<PaginatedResponse<Registration>>(
        `/registration/list?page=${page}&limit=${limit}&search=${searchTerm}&filter=${serviceTypeFilter?.name}&subtype=${subTypeFilter?.name}&subsubtype=${subSubTypeFilter?.name}&status=${statusFilter}&anonymous=${anonymousFilter}&paymentMethod=${paymentMethodFilter}&invoiced=${invoicedFilter}${monthParam}${yearParam}`,
      );
      // console.log('Success to fetch registrations:', res);
      setRegistrations(res.data);
      setTotalItems(res.metadata.totalItems);
      setLastSyncTime(dayjs());
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    searchTerm,
    serviceTypeFilter?.name,
    subTypeFilter?.name,
    subSubTypeFilter?.name,
    statusFilter,
    anonymousFilter,
    paymentMethodFilter,
    invoicedFilter,
    filterMonth,
    filterYear,
  ]);

  const fetchServiceOptions = async () => {
    const res = await apiGet<Response<ServiceType>>(`/service/list`);
    setServiceOptions(res.data);
  };
  // Hooks
  useEffect(() => {
    fetchRegistrations();
  }, [
    page,
    limit,
    serviceTypeFilter,
    subTypeFilter,
    subSubTypeFilter,
    statusFilter,
    anonymousFilter,
    paymentMethodFilter,
    invoicedFilter,
    filterMonth,
    filterYear,
  ]);

  // ==============================
  // Update 06/06/2025: Fetch registrations every 3 seconds
  // ==============================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRegistrations(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchRegistrations]);

  useEffect(() => {
    fetchServiceOptions();
  }, []);

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };

    setServiceTypeFilter(selectedServiceType);
    setPage(1);
  };
  const handleFilterSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === serviceTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };
    const selectedSubtype = selectedServiceType.subtypes.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      subsubtypes: [],
    };

    setSubTypeFilter(selectedSubtype);
    setPage(1);
  };
  const handleFilterSubSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedServiceType = serviceOptions.find(
      (option) => option.id === serviceTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subtypes: [],
    };
    const selectedSubtype = selectedServiceType.subtypes.find(
      (option) => option.id === subTypeFilter.id,
    ) || {
      id: '-1',
      name: 'All',
      subsubtypes: [],
    };
    const selectedSubSubtype = selectedSubtype.subsubtypes.find(
      (option) => option.id === newValue,
    ) || {
      id: '-1',
      name: 'All',
      serviceFor: 'CHILD',
      price: 0,
      duration: 0,
    };

    setSubSubTypeFilter(selectedSubSubtype);
    setPage(1);
  };

  const calculateAge = (dob: string | Date) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // const handleFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setUserTypeFilter(event.target.value as string);
  //   setPage(1);
  //   notifyFilterSuccess();
  // };

  // const handleRequestSort = (property: keyof User) => {
  //   const isAsc = orderBy === property && order === "asc";
  //   setOrder(isAsc ? "desc" : "asc");
  //   setOrderBy(property);
  // };

  const [confirmInvoiceDialogOpen, setConfirmInvoiceDialogOpen] =
    useState(false);
  const handlePrintInvoice = async (reg: Registration) => {
    setCreateInvoiceLoading(true);
    try {
      // const invoiceId =
      //   reg.subsubtype.serviceFor === 'GROUP'
      //     ? await minvoiceApi.createInvoiceForGroupService(reg)
      //     : await minvoiceApi.createInvoice(reg);

      // if (!invoiceId) {
      //   throw new Error('Failed to create invoice');
      // } else {
      //   await apiPut<RegistrationResponse, any>(
      //     `/registration/invoice-status/${reg.id}`,
      //     {},
      //   );
      // }
      await apiPost<RegistrationResponse, any>(
        `/registration/export-invoice/${reg.id}`,
        {},
      );

      setRegistrations((prev) =>
        prev.map((registration) => {
          if (registration.id === reg.id) {
            return {
              ...registration,
              isInvoiced: true,
            };
          }
          return registration;
        }),
      );

      // const blob = new Blob([atob(xmlData)], { type: 'text/xml' });
      // const url = window.URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `draft_invoice_${invoiceId}.xml`;
      // a.click();
      // window.URL.revokeObjectURL(url);

      setSelectedRegistrations([]);
      setSelectedUserId(null);
      notifyAPIClient('Invoice created successfully!');
    } catch (err: any) {
      notifyAPIClient(err.message || 'Failed to create invoice');
    } finally {
      setCreateInvoiceLoading(false);
    }
  };

  // ==============================
  // Update 09/06/2025: Clear all filters
  // ==============================
  const clearFilters = () => {
    setSearchTerm('');
    setServiceTypeFilter({ id: '-1', name: 'All', subtypes: [] });
    setSubTypeFilter({ id: '-1', name: 'All', subsubtypes: [] });
    setSubSubTypeFilter({
      id: '-1',
      name: 'All',
      serviceFor: 'CHILD',
      price: 0,
      duration: 0,
    });
    setStatusFilter('all');
    setAnonymousFilter('all');
    setPaymentMethodFilter('all');
    setInvoicedFilter('all');
    setSelectedRegistration(null);
    setFilterMonth(null);
    setFilterYear(null);

    setPage(1);
    setLimit(10);
  };

  const handlePrintMultipleInvoices = async () => {
    setCreateInvoiceLoading(true);
    try {
      await apiPost<RegistrationResponse, any>(
        '/registration/export-invoices',
        {
          ids: selectedRegistrations,
        },
      );

      setRegistrations((prev) =>
        prev.map((registration) => {
          if (selectedRegistrations.includes(registration.id)) {
            return {
              ...registration,
              isInvoiced: true,
            };
          }
          return registration;
        }),
      );

      setSelectedRegistrations([]);
      setSelectedUserId(null);
      notifyAPIClient('Invoices created successfully!');
    } catch (err: any) {
      notifyAPIClient(err.message || 'Failed to create invoices');
    } finally {
      setCreateInvoiceLoading(false);
    }
  };

  const emptyRows = limit - registrations.length;

  const handleCloseRegistrationInfoDialog = () => {
    setSelectedRegistration(null);
    setOpenRegistrationInfoDialog(false);
  };

  const handleDeleteRegistration = async (user: Registration) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/registration/delete/${user.id}?force=${false}`,
      );
      notifyAPIClient(res.message);
      setSelectedRegistration(null);
      fetchRegistrations();
    } catch (error) {
      console.error('Failed to delete registration:', error);
    }
  };

  // ==============================
  // Update 30/07/2025: Download registration list
  // ==============================
  const handleDownloadRegistrationList = async () => {
    setDownloadLoading(true);
    try {
      const monthParam = filterMonth ? `&month=${filterMonth}` : '';
      const yearParam = filterYear ? `&year=${filterYear}` : '';

      const response = await apiGet<Blob>(
        `/registration/download?search=${searchTerm}&filter=${serviceTypeFilter?.name}&subtype=${subTypeFilter?.name}&subsubtype=${subSubTypeFilter?.name}&status=${statusFilter}&anonymous=${anonymousFilter}&paymentMethod=${paymentMethodFilter}&invoiced=${invoicedFilter}${monthParam}${yearParam}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as any], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename =
        filterMonth && filterYear
          ? `registration_list_${filterYear}-${String(filterMonth).padStart(2, '0')}.xlsx`
          : `registration_list_${dayjs().format('YYYY-MM')}.xlsx`;

      saveAs(blob, filename);
    } catch (error) {
      console.error('Download data failed:', error);
      notifyAPIClient('Failed to download data. Please try again later.');
    } finally {
      setDownloadLoading(false);
    }
  };

  return {
    registrations,
    page,
    limit,
    totalItems,
    searchTerm,
    serviceTypeFilter,
    subTypeFilter,
    setSubTypeFilter,
    subSubTypeFilter,
    statusFilter,
    setStatusFilter,
    anonymousFilter,
    setAnonymousFilter,
    paymentMethodFilter,
    setPaymentMethodFilter,
    invoicedFilter,
    setInvoicedFilter,
    emptyRows,
    calculateAge,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleFilterSubtypeChange,
    handleFilterSubSubtypeChange,
    clearFilters,

    fetchRegistrations,
    setPage,
    serviceOptions,

    openRegistrationInfoDialog,
    setOpenRegistrationInfoDialog,
    selectedRegistration,
    setSelectedRegistration,
    handleCloseRegistrationInfoDialog,

    confirmInvoiceDialogOpen,
    setConfirmInvoiceDialogOpen,
    handlePrintInvoice,
    handlePrintMultipleInvoices,
    createInvoiceLoading,

    handleDeleteRegistration,
    openDeleteRegistrationDialog,
    setOpenDeleteRegistrationDialog,

    selectedRegistrations,
    setSelectedRegistrations,
    handleSelectRegistration,
    selectedUserId,
    setSelectedUserId,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,

    loading,

    handleDownloadRegistrationList,
    downloadLoading,

    filterMonth,
    setFilterMonth,
    filterYear,
    setFilterYear,
  };
};
